import {SerializedStyles} from '@emotion/react';
import {css, useTheme, IconButton, IconButtonProps, Typography} from '@mui/material';
import SvgIcon, {SvgIconProps} from '@mui/material/SvgIcon';
import React from 'react';
import {useIntl} from 'react-intl';
import {getIconComponentByName} from 'src/js/common/icons/icon_helpers';
import {GetStylesFunction} from 'src/js/common/types';
import {ColorUtils, TranslateUtils} from 'src/js/common/utils';
import {useMuiAnchor} from 'src/js/hooks';

import {DlMuiPopper} from './DlMuiPopper';

interface OwnProps extends IconButtonProps {
  iconName?: string;
  iconTitle: string;
  inputCss?: SerializedStyles;
  iconBackgroundCss?: SerializedStyles;
  svgIconInputCss?: SerializedStyles;
  svgIconProps?: SvgIconProps;
  isSelected?: boolean;
}

type Props = OwnProps;

export const DlMuiAddButton: React.FC<Props> = (props) => {
  const {iconName = 'plusIcon', inputCss, iconTitle, iconBackgroundCss, svgIconInputCss, svgIconProps, isSelected, ...restProps} = props;
  const theme = useTheme();
  const intl = useIntl();
  const styles = getStyles({
    theme,
    params: {inputCss, iconBackgroundCss, svgIconInputCss, isSelected},
  });

  const translatedIconTitle = intl && TranslateUtils.translate(iconTitle, intl);
  const hasTooltip = !!translatedIconTitle;
  const {mouseEventEnter, mouseEventLeave, open, anchorEl} = useMuiAnchor(hasTooltip);

  let RenderedIconComponent;
  if (iconName) {
    RenderedIconComponent = getIconComponentByName(iconName);
  }

  return (
    <>
      <IconButton onMouseEnter={mouseEventEnter} onMouseLeave={mouseEventLeave} css={styles.iconButton} {...restProps}>
        <div css={styles.iconBackground}>
          <SvgIcon css={styles.svgIcon} component={RenderedIconComponent} viewBox='3 3 14 14' {...svgIconProps} />
        </div>
      </IconButton>
      <DlMuiPopper hasTooltip={hasTooltip} anchorEl={anchorEl} open={open}>
        <Typography variant={'popper'}>{translatedIconTitle}</Typography>
      </DlMuiPopper>
    </>
  );
};

DlMuiAddButton.displayName = 'DlMuiAddButton';

const getStyles: GetStylesFunction = (options) => {
  const color = options?.theme?.palette.custom.theme['500'];
  const darkColor = options?.theme?.palette.custom.theme['700'];
  const backgroundColor = (hex) => {
    const rgb = ColorUtils.hexToRgb(hex);
    return ColorUtils.rgbToRgbaString(rgb as any, 0.4);
  };
  const colorBackground = backgroundColor(color);
  const darkColorBackground = backgroundColor(darkColor);
  return {
    iconButton: css(
      {
        cursor: 'pointer',
        padding: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 16,
      },
      options?.params?.inputCss
    ),
    iconBackground: css(
      {
        'cursor': 'pointer',
        'width': 16,
        'height': 16,
        'borderRadius': 8,
        'background': options?.params?.isSelected ? darkColorBackground : colorBackground,
        'color': options?.params?.isSelected ? darkColor : color,
        '&:hover': {
          background: darkColorBackground,
          color: darkColor,
        },
      },
      options?.params?.iconBackgroundCss
    ),
    svgIcon: css(
      {
        cursor: 'pointer',
        fontSize: '1em',
        verticalAlign: 'top',
      },
      options?.params?.svgIconInputCss
    ),
  };
};
